import React, { useState, useEffect } from 'react';
import { RefreshCw, Trash2 } from 'lucide-react';
import { cotizacionesApi, indicesApi } from '../../lib/api';
import { formatPeriodo, getPreviousPeriods, formatNumber } from '../../lib/utils';
import { LoadingState, ErrorState } from '../../components/common/EmptyState';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { PeriodSelector } from '../../components/common/PeriodSelector';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

// Celda editable inline para USD / Índice CAC.
const EditableCell = ({ value, prefix = '', decimals = 2, onSave, testid }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const [saving, setSaving] = useState(false);

  const start = () => { setVal(value != null ? String(value) : ''); setEditing(true); };
  const commit = async () => {
    if (saving) return;
    const raw = (val || '').trim();
    const num = parseFloat(raw.replace(/\./g, '').replace(',', '.'));
    if (raw === '' || isNaN(num) || num < 0) { setEditing(false); return; }
    if (value != null && num === Number(value)) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(num);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar. Se mantuvo el valor anterior.');
      setEditing(false); // restaura el valor anterior (no se pisa el estado)
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <input
        autoFocus
        type="text"
        value={val}
        disabled={saving}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
        className="w-28 text-right border border-blue-400 rounded px-1.5 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
        data-testid={`${testid}-input`}
      />
    );
  }
  return (
    <button
      onClick={start}
      className="w-full text-right text-slate-700 hover:bg-blue-50 rounded px-1.5 py-0.5 transition-colors"
      data-testid={testid}
    >
      {value != null && value !== '' && Number(value) > 0
        ? `${prefix}${formatNumber(value, decimals)}`
        : <span className="text-blue-500 text-xs">Agregar</span>}
    </button>
  );
};

export const AnalistaCotizaciones = () => {
  const [cotizaciones, setCotizaciones] = useState([]);
  const [ipcData, setIpcData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingIPC, setSyncingIPC] = useState(false);
  const [error, setError] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ periodo: '', usd_ars: '', ipc_valor: '' });
  const [editingCotizacionId, setEditingCotizacionId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    (async () => {
      await fetchCotizaciones();
      // Auto-sincroniza el IPC publicado por INDEC hacia la colección `cotizaciones`
      // (idempotente, no pisa valores manuales) para que "lo que se ve" sea también
      // "lo que el cálculo usa". Silencioso: si la API externa no responde, no molesta.
      try {
        await indicesApi.syncIPC();
        await fetchCotizaciones();
      } catch (e) {
        // INDEC no disponible: se mantiene lo persistido; el usuario puede sincronizar manualmente.
      }
      await fetchIPCData();
    })();
  }, []);

  const fetchCotizaciones = async () => {
    try {
      setLoading(true);
      const response = await cotizacionesApi.list();
      setCotizaciones(response.data);
    } catch (err) {
      console.error('Error fetching cotizaciones:', err);
      setError('Error al cargar las cotizaciones');
    } finally {
      setLoading(false);
    }
  };

  const fetchIPCData = async () => {
    try {
      const response = await indicesApi.getIPC();
      setIpcData(response.data);
    } catch (err) {
      console.error('Error fetching IPC:', err);
    }
  };

  const handleSyncIPC = async () => {
    setSyncingIPC(true);
    try {
      const r = await indicesApi.syncIPC();
      const d = r?.data || {};
      const msg = d.mensaje || `IPC sincronizado: ${d.creados || 0} nuevos, ${d.actualizados || 0} actualizados, ${d.omitidos || 0} omitidos`;
      toast.success(msg);
      fetchIPCData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      console.error('Sync IPC error:', status, detail, err);
      toast.error(detail || `Error al sincronizar IPC${status ? ` (${status})` : ''}`);
    } finally {
      setSyncingIPC(false);
    }
  };

  // Guardado inline de USD / Índice CAC (upsert por período, no pisa otros campos).
  const saveCell = async (periodo, field, num) => {
    await cotizacionesApi.createOrUpdate({ periodo, [field]: num });
    await fetchCotizaciones();
  };

  const handleOpenEdit = (cotizacion) => {
    setFormData({
      periodo: cotizacion.periodo,
      usd_ars: cotizacion.usd_ars?.toString() || '',
      ipc_valor: cotizacion.ipc_valor?.toString() || '',
    });
    setEditingCotizacionId(cotizacion.cotizacion_id);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await cotizacionesApi.createOrUpdate({
        periodo: formData.periodo,
        usd_ars: formData.usd_ars ? parseFloat(formData.usd_ars) : null,
        ipc_valor: formData.ipc_valor ? parseFloat(formData.ipc_valor) : null,
      });
      toast.success('Guardado correctamente');
      setIsDialogOpen(false);
      fetchCotizaciones();
    } catch (err) {
      console.error('Error saving cotizacion:', err);
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const existingPeriods = cotizaciones.map(c => c.periodo);

  const handleDelete = async () => {
    if (!editingCotizacionId) return;
    setDeleting(true);
    try {
      await cotizacionesApi.delete(editingCotizacionId);
      toast.success('Cotización eliminada');
      setIsDialogOpen(false);
      setIsDeleteDialogOpen(false);
      fetchCotizaciones();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  const availablePeriods = getPreviousPeriods(24);

  if (loading) return <LoadingState message="Cargando índices y cotizaciones..." />;
  if (error) return <ErrorState message={error} onRetry={fetchCotizaciones} />;

  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Índices y Cotizaciones</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSyncIPC} disabled={syncingIPC} data-testid="sync-ipc-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${syncingIPC ? 'animate-spin' : ''}`} />
            {syncingIPC ? 'Sincronizando...' : 'Sincronizar IPC'}
          </Button>
        </div>
      </div>

      {(() => {
        // Construir lista unificada de períodos
        const PER_COUNT = 24;
        const periodSet = new Set();
        const now = new Date();
        for (let i = 0; i < PER_COUNT; i++) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          periodSet.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
        }
        cotizaciones.forEach(c => periodSet.add(c.periodo));
        ipcData.forEach(r => periodSet.add(r.periodo));
        const periodos = Array.from(periodSet).sort().reverse();

        const cotByPer = Object.fromEntries(cotizaciones.map(c => [c.periodo, c]));
        const ipcByPer = Object.fromEntries(ipcData.map(r => [r.periodo, r.valor]));

        const getIPC = (per) => {
          const v = ipcByPer[per];
          if (v != null) return v;
          // Fallback: si está guardado solo en cotizaciones.ipc_valor
          const cv = cotByPer[per];
          return cv?.ipc_valor != null ? cv.ipc_valor : null;
        };
        const shiftPer = (per, deltaMonths) => {
          const [y, m] = per.split('-').map(Number);
          const d = new Date(y, m - 1 + deltaMonths, 1);
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        };
        const calcVar = (a, b) => (a != null && b != null && b !== 0) ? ((a / b - 1) * 100) : null;

        return (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Período</th>
                  <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">USD</th>
                  <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Índice CAC</th>
                  <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">IPC</th>
                  <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Var. IPC mensual</th>
                  <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Var. IPC anual</th>
                </tr>
              </thead>
              <tbody>
                {periodos.map((per) => {
                  const cot = cotByPer[per];
                  const ipc = getIPC(per);
                  const ipcPrev = getIPC(shiftPer(per, -1));
                  const ipcAnio = getIPC(shiftPer(per, -12));
                  const vMen = calcVar(ipc, ipcPrev);
                  const vAnu = calcVar(ipc, ipcAnio);
                  return (
                    <tr key={per} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`row-${per}`}>
                      <td className="py-2.5 px-4 font-medium text-slate-900 whitespace-nowrap">{formatPeriodo(per)}</td>
                      <td className="py-2.5 px-4 text-right whitespace-nowrap">
                        <EditableCell
                          value={cot?.usd_ars}
                          prefix="$ "
                          decimals={2}
                          onSave={(num) => saveCell(per, 'usd_ars', num)}
                          testid={`usd-${per}`}
                        />
                      </td>
                      <td className="py-2.5 px-4 text-right whitespace-nowrap">
                        <EditableCell
                          value={cot?.indice_cac}
                          prefix=""
                          decimals={2}
                          onSave={(num) => saveCell(per, 'indice_cac', num)}
                          testid={`cac-${per}`}
                        />
                      </td>
                      <td className="py-2.5 px-4 text-right text-slate-700 whitespace-nowrap">
                        {ipc != null ? formatNumber(ipc, 2) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="py-2.5 px-4 text-right whitespace-nowrap">
                        {vMen != null ? (
                          <span className={vMen >= 0 ? 'text-emerald-700' : 'text-red-700'}>{vMen.toFixed(2)}%</span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="py-2.5 px-4 text-right whitespace-nowrap">
                        {vAnu != null ? (
                          <span className={vAnu >= 0 ? 'text-emerald-700' : 'text-red-700'}>{vAnu.toFixed(2)}%</span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {existingPeriods.includes(formData.periodo) ? 'Editar' : 'Nuevo'} Índice/Cotización
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Período *</Label>
              <PeriodSelector
                value={formData.periodo}
                onChange={(value) => setFormData({ ...formData, periodo: value })}
                periods={availablePeriods}
                placeholder="Seleccionar período"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="usd_ars">Cotización USD/ARS</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                <Input
                  id="usd_ars"
                  type="number"
                  step="0.01"
                  value={formData.usd_ars}
                  onChange={(e) => setFormData({ ...formData, usd_ars: e.target.value })}
                  placeholder="1050.00"
                  className="pl-7"
                />
              </div>
              <p className="text-xs text-slate-500">Valor de 1 USD en pesos argentinos</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ipc_valor">Índice IPC</Label>
              <Input
                id="ipc_valor"
                type="number"
                step="0.01"
                value={formData.ipc_valor}
                onChange={(e) => setFormData({ ...formData, ipc_valor: e.target.value })}
                placeholder="450.00"
              />
              <p className="text-xs text-slate-500">Índice IPC (Base dic 2016 = 100)</p>
            </div>
            
            <DialogFooter className="flex justify-between sm:justify-between">
              {editingCotizacionId ? (
                <Button type="button" variant="destructive" onClick={() => setIsDeleteDialogOpen(true)} disabled={deleting} data-testid="delete-cotizacion-btn">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Borrar
                </Button>
              ) : <div />}
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
                <Button type="submit" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar cotización?</AlertDialogTitle>
            <AlertDialogDescription>
              Estás por eliminar la cotización del período <strong>{formatPeriodo(formData.periodo)}</strong>.
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" disabled={deleting}>
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AnalistaCotizaciones;
